import * as React from 'react';
import { useMutation } from 'react-query';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { routes } from '~/constants/routes';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { getAllAgents } from '~/redux/actions/conversationDataR';
import { handleErrors, showToast } from '~/utils';
import { Api } from '~/utils/http/api';

type CreateAgentBody = {
  avatar: string;
  agent_name: string;
  is_template: boolean;
  is_public: boolean;
  account_id: number;
};

type Params = {
  onClose: () => void;
};

export const useCreateAgentModal = ({ onClose }: Params) => {
  const user = useCurrentUser();
  const dispatch = useDispatch();
  const history = useHistory();

  const [agentName, setAgentName] = React.useState('');
  const [avatarUrl, setAvatarUrl] = React.useState('');
  const [showImageDialog, setShowImageDialog] = React.useState(false);
  const [nameError, setNameError] = React.useState(false);

  const createAgentMutation = useMutation({
    mutationFn: (body: CreateAgentBody) => Api.agent.createAgent({ body }),
    onSuccess: ({ data }) => {
      showToast('success', 'Chatbot created successfully!');
      dispatch(getAllAgents());
      onClose();
      history.push(routes.chatbotDetails.get(data.id!));
    },
    onError: (error: any) => {
      if (error?.response?.status === 400) {
        showToast('error', error?.response?.data?.message);
      } else {
        handleErrors(error, 'Failed to create chatbot');
      }
    },
  });

  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAgentName(event.target.value);
    validateName(event.target.value);
  };

  const validateName = (name: string) => {
    setNameError(!name || name.trim().length === 0);
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!agentName.trim()) {
      setNameError(true);
      return;
    }

    const body: CreateAgentBody = {
      avatar: avatarUrl,
      agent_name: agentName.trim(),
      is_template: false,
      is_public: false,
      account_id: user.account_id,
    };

    createAgentMutation.mutate(body);
  };

  const handleOpenImageDialog = () => {
    setShowImageDialog(true);
  };

  const handleCloseImageDialog = () => {
    setShowImageDialog(false);
  };

  const handleAvatarChange = (url: string) => {
    setAvatarUrl(url);
  };

  const handleClose = () => {
    setAgentName('');
    setAvatarUrl('');
    setNameError(false);
    onClose();
  };

  const isFormValid = agentName.trim().length > 0 && !nameError;

  return {
    agentName,
    avatarUrl,
    showImageDialog,
    nameError,
    isLoading: createAgentMutation.isLoading,
    isFormValid,
    handleNameChange,
    handleSubmit,
    handleOpenImageDialog,
    handleCloseImageDialog,
    handleAvatarChange,
    handleClose,
    validateName,
  };
};
